import { RestaurantEventDisclaimer } from '@shared/types/restaurantEvents';
import { api } from '../api';
import { type RestaurantDetails } from '../restaurant/apiHelpers';

export interface PayLinkAppliedDiscount {
  couponCode: string | null;
  vipTierName: string | null;
  amountCents: number;
  originalPriceCents: number;
}

export interface PayLinkReservation {
  id: string;
  date: string;
  time: string;
  guestCount: number;
  listing: { publicName: string } | null;
  /**
   * The amounts stored when the link was created, so the guest sees what will be
   * charged. An event reservation also carries its line items.
   */
  transaction: {
    price: number;
    fee?: number;
    tax?: number;
    total?: number;
    lineItems?: TransactionLineItem[];
  } | null;
  appliedDiscount: PayLinkAppliedDiscount | null;
  restaurantEvent?: { id: string; name: string };
  restaurant: RestaurantDetails;
}

export const getPayLinkReservation = async (
  reservationId: string,
): Promise<PayLinkReservation> =>
  api.get(`/reservations/${reservationId}/pay-link`);

interface CreateReservationRequest {
  cancellationPolicyId: string | null;
  date: string;
  guestCount: number;
  listingId: string;
  availabilityPrice: number;
  time: string;
  referralCode: string | null;
  restaurantEventId?: string;
  transactionLineItems: TransactionLineItem[];
  couponCode?: string | null;
  marketingOptIn?: boolean;
}

export interface TransactionLineItem {
  name: string;
  pricePerUnit: number;
  quantity: number;
}

export interface CreatedReservation {
  fee: number;
  id: string;
  price: number;
  receiptNumber: string;
  tax: number;
  total: number;
  type: 'initial purchase';
}

export type CreateReservationResponse = CreatedReservation & {
  paymentIntentClientSecret?: string;
  setupIntentClientSecret?: string;
  lineItems: TransactionLineItem[];
};

export const bookReservation = async (
  reservationDetails: CreateReservationRequest,
): Promise<CreateReservationResponse> =>
  api.post('/reservations', reservationDetails);

export interface CompleteReservationPaymentRequest {
  cancellationPolicyId: string | null;
  couponCode: string | null;
}

export type CompleteReservationPaymentResponse = CreatedReservation & {
  paymentIntentClientSecret?: string;
  setupIntentClientSecret?: string;
  lineItems: TransactionLineItem[];
};

export const completeReservationPayment = async (
  reservationId: string,
  body: CompleteReservationPaymentRequest,
): Promise<CompleteReservationPaymentResponse> =>
  api.post(`/reservations/${reservationId}/complete-payment`, body);

export interface CompleteEventReservationPaymentResponse {
  id: string;
  paymentIntentClientSecret: string;
}

export const completeEventReservationPayment = async (
  reservationId: string,
): Promise<CompleteEventReservationPaymentResponse> =>
  api.post(`/reservations/${reservationId}/complete-event-payment`);

export interface ValidateCheckoutCouponResponse {
  valid: boolean;
  code?: string;
  amountOffTablePercentage?: number;
  discountAmount?: number;
  invalidReason?: 'resale';
}

export const validateCheckoutCoupon = async ({
  restaurantId,
  code,
  initialTablePrice,
  isResale,
}: {
  restaurantId: string;
  code: string;
  initialTablePrice: number;
  isResale: boolean;
}): Promise<ValidateCheckoutCouponResponse> =>
  api.post(`/restaurants/${restaurantId}/coupons/validate-checkout`, {
    code,
    initialTablePrice,
    isResale,
  });

interface CreateOfferRequest {
  date: string;
  expectedCancellationPolicyId: string | null;
  guestCount: number;
  listingId: string;
  offerAmount: number;
  paymentMethodId?: string;
  confirmationTokenId?: string;
  setupFutureCardUsage: boolean;
  time: string;
}

export interface CreatedOffer {
  fee: number;
  id: string;
  price: number;
  tax: number;
  total: number;
}

export const createOffer = async (
  payload: CreateOfferRequest,
): Promise<CreatedOffer> => api.post('/offers', payload);

export const deletePaymentMethod = async (id: string): Promise<void> =>
  api.delete(`/payment-methods/${id}`);

export interface PaymentMethod {
  brand: string;
  expirationMonth: number;
  expirationYear: number;
  id: string;
  last4: string;
}

export const getPaymentMethods = async (): Promise<PaymentMethod[]> =>
  api.get('/payment-methods');

export const getRestaurantEventDisclaimers = async ({
  restaurantId,
  restaurantEventId,
}: {
  restaurantId: string;
  restaurantEventId: string;
}) => {
  return api.get<RestaurantEventDisclaimer[]>(
    `/restaurants/${restaurantId}/restaurant-events/${restaurantEventId}/disclaimers`,
  );
};

export const getGuestMarketingOptInForRestaurant = async (
  restaurantId: string,
): Promise<{ optedIn: boolean }> =>
  api.get(`/restaurants/${restaurantId}/guest-marketing-opt-in`);
